import React from 'react';
import { headerTitle } from './Table';

// SortableTh — a standardized sortable table header cell. Renders a plain <th> when not
// sortable, or a clickable header with the app's shared sort icon when `sortKey` + `onSort`
// are provided. Same icon language used by DataTable and the QP task table:
//   inactive → faint up/down arrows · active → directional caret.
// Use inside a raw <thead><tr> so mixed sortable/non-sortable columns stay consistent.
export interface SortableThProps
  extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'onClick'> {
  label: React.ReactNode;
  /** Omit to render a non-sortable header. */
  sortKey?: string;
  activeKey?: string | null;
  direction?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

export const SortableTh: React.FC<SortableThProps> = ({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
  className = '',
  children,
  ...rest
}) => {
  const sortable = !!sortKey && !!onSort;
  const active = sortable && activeKey === sortKey;
  const icon = !sortable ? null : !active ? (
    <i className="bi bi-arrow-down-up tw-ml-1 tw-text-content-muted" aria-hidden="true" />
  ) : direction === 'asc' ? (
    <i className="bi bi-sort-down-alt tw-ml-1" aria-hidden="true" />
  ) : (
    <i className="bi bi-sort-down tw-ml-1" aria-hidden="true" />
  );

  // The control is a real <button> rather than a click handler on the <th>: a bare onClick is
  // unreachable by keyboard and invisible to assistive tech. The button carries the interaction
  // and focus ring; the <th> keeps aria-sort, which belongs on the header cell itself.
  return (
    <th
      className={`${sortable ? 'tw-select-none' : ''} ${className}`}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
      {...rest}
      // Headers are single-line and ellipsized, so a long label would otherwise be unreadable.
      // Spread first so an explicit `title` from the caller still wins.
      title={rest.title ?? headerTitle(label)}
    >
      {sortable ? (
        <button
          type="button"
          onClick={() => onSort!(sortKey!, active && direction === 'asc' ? 'desc' : 'asc')}
          className="tw-inline-flex tw-w-full tw-cursor-pointer tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-font-[inherit] tw-text-inherit focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-line-focus"
        >
          {label}
          {icon}
        </button>
      ) : (
        <>
          {label}
          {icon}
        </>
      )}
      {children}
    </th>
  );
};

export default SortableTh;
